// When is a write to the pedigree an EDIT — i.e. something that should take the
// edit lock and pin the tab — and when is it just the app writing to itself?
//
// TGCLOUD-1665. Opening a pedigree in the multi-tab workspace is meant to be
// view-only, but three writers run during the first layout pass of a freshly
// opened family (the settled-order capture, the legacy pixel-pin upgrade and the
// invalid-pin sweep) and land in the same layout-autosave effects a user's node
// drag does. Treating those as edits meant merely LOOKING at a pedigree took the
// lock, pinned the tab and wrote a "Pedigree lock acquired" audit entry.
//
// The rules live here, rather than only inside PedigreeChart/PedigreeChartView,
// so they can be pinned by tests without mounting a 2,000-line chart — the same
// reasoning as familyLoadPolicy.js next door.
//
// TWO INDEPENDENT GATES, deliberately failing in opposite directions:
//
//   1. createAutomaticWriteLatch — a BLOCKLIST. Each automatic writer announces
//      itself; anything that doesn't announce is treated as a user edit. Precise,
//      but only covers writers we know about.
//   2. shouldSignalEdit's `hasInteracted` — an ALLOWLIST. A layout write that
//      arrives before the user has touched this pedigree cannot be theirs,
//      whoever wrote it. Covers the writers we don't know about yet, which is
//      how this bug got in.

// Tracks "the next write to <kind> comes from the engine, not the user".
//
// One latch slot per layout state, and armed only for the state the caller is
// about to write. A single shared flag would be a bug: the settled-order capture
// would arm it, the ORDER effect would run, and the flag would still be set when
// the user's next genuine node drag arrived — silently swallowing a real edit and
// leaving them editing without the lock.
export function createAutomaticWriteLatch() {
  const armed = { positions: false, order: false };
  return {
    // Called by an automatic writer immediately before its setState.
    // An unrecognised kind arms nothing, so that write is treated as a user edit
    // and takes the lock — the safe direction to fail in.
    arm(kind) {
      if (kind === 'positions' || kind === 'order') armed[kind] = true;
    },
    // Called by the autosave effect for `kind`. Returns whether this write was
    // automatic, and disarms — so the write AFTER an automatic one is judged on
    // its own merits rather than inheriting the suppression.
    consume(kind) {
      const wasAutomatic = armed[kind] === true;
      armed[kind] = false;
      return wasAutomatic;
    },
    // Test/debug visibility only; nothing in the app should branch on this.
    isArmed(kind) {
      return armed[kind] === true;
    }
  };
}

// Should this write be reported up to the workspace as a user edit?
//
// `source` is 'layout' (node moves, ordering, arrange-lock — the paths the engine
// also writes through) or 'data' (a clinical save, which no automatic writer can
// produce). The interaction gate applies to 'layout' only: a data save always
// follows user input, and gating it could only ever fail in the dangerous
// direction — real record edits committed without holding the lock.
export function shouldSignalEdit({
  workspaceMode,
  isActive,
  source,
  hasInteracted
}) {
  // Outside the workspace the chart takes the lock on open anyway, and a
  // background tab must never acquire on behalf of a tab the user isn't looking
  // at (N open tabs would otherwise fight over one lock).
  if (!workspaceMode || !isActive) return false;
  if (source === 'layout' && !hasInteracted) return false;
  return true;
}
